/**
 * KasaDefterPage — Kasa Günlük Defter (Faz A, Görev 9)
 *
 * Kullanıcının Excel `İŞLETME GELİRİ` sayfasının canlı karşılığı: seçili
 * lokasyon + ay için her TAKVİM GÜNÜ bir satır olan yoğun bir defter grid'i.
 * Açılış→Devir nakit zinciri ay boyunca yukarıdan aşağı okunur; Modpos'tan
 * gelen otomatik kolonlar (kart/Σ terminal/bahşiş/teslim/yemek kartı/KDV)
 * gri tonda, insan girdisi kolonlar (açılış/nakit/devir/gider) normal tonda.
 *
 * Veri kaynağı: GET /api/kasa-defter/ay?lokasyon_id=&yil=&ay=
 *   → { yil, ay, lokasyon_id, gunler: [...], ozet: {...} }
 * Her gunler[i]: tarih, kapanis_id, durum, kasa_acilis, kasa_nakit, giderler,
 *   teslim_edilen_nakit, kasa_devri, modpos_kredi_kart, kredi_karti_toplami,
 *   kart_bahsis, yemek_karti_toplami, yemek_karti_kdv.
 *
 * Anayasa Madde 1: Lokasyon dropdown'u `/api/lokasyonlar`'dan (aktif olanlar)
 * beslenir — hardcoded liste yok. Yıl/ay takvim gerçeğidir, hesaplanır.
 * Nakit harcama tek toplam olarak girilir; detaylı/kategorili gider Gider Girişi sayfasından.
 *
 * Görev 10 (bu dosya): Gün-detay editörü — bir gün satırına tıklayınca
 * `GET /api/kasa-defter/gun` ile gün açılır (yoksa TASLAK olarak yaratılır,
 * modpos + dünkü devir auto-fill edilir, terminal/sağlayıcı satırları hazırlanır).
 * Sağ kayan panelde muhasebeci ELLE giremeyeceği verileri girer: 6 terminal Z,
 * sayılan nakit, kasa devri, tek toplam nakit harcama. Modpos değerleri (kredi kart + yemek
 * kartı sağlayıcı) gri/salt-okunur ön-doldurulmuş gelir. Canlı önizleme
 * (Q/J/Z/Teslim/KDV) `hesapla_gun` formülünü birebir aynalar; kaydet (`PUT`)
 * backend'i otorite olarak yeniden hesaplatır ve grid'i tazeler.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const AYLAR = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];
const HAFTA_GUNLERI = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

const BRAND = '#c62828';
const AUTO_TONE = '#94a3b8';   // sistem türevi kolonlar (modpos) — soluk
const MANUAL_TONE = '#1f2937'; // insan girdisi kolonlar — koyu
// Nakit zinciri (açılış→devir) vurgusu — sakin slate. Kırmızı (BRAND) artık
// YALNIZ uyarı/hata/aksiyon (eksik fark, Kaydet, bugün, hata kutusu) için ayrıldı;
// her vurgu kırmızı olunca gerçek uyarı öne çıkamıyordu.
const CASH_ACCENT = '#475569'; // slate-600 — nakit zinciri sol kenar/işaret vurgusu

// Nakit mutabakat farkı — işaret bazlı 3 renk (artı/eksi ayrımı):
//   0  → uyumlu      (yeşil)
//   +  → fazla       (canlı turuncu) — kasada beklenenden çok nakit
//   −  → eksik/açık  (kırmızı)       — kasada nakit açığı (en kritik)
// Turuncu kasıtlı CANLI (#ea580c): koyu kehribar kırmızıdan ayrışmıyordu.
// Ayrıca grid'de ▲/▼ ok ile yön renkten BAĞIMSIZ da okunur (renk körlüğü güvenli).
const FARK_OK_TONE = '#16a34a';      // uyumlu — yeşil
const FARK_FAZLA_TONE = '#ea580c';   // fazla — canlı turuncu (kırmızıdan net ayrışır)
const FARK_EKSIK_TONE = BRAND;       // eksik — kırmızı

// tr-TR para formatı: 1.250,75 — sıfır/boş güvenli
const formatTl = (n) => {
    const v = Number(n) || 0;
    return v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const isoToParts = (iso) => {
    // 'YYYY-MM-DD' → {gun, haftaGunu} (UTC parse: TZ kayması olmasın)
    const [y, m, d] = String(iso).split('-').map(Number);
    const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
    return { gun: d, haftaGunu: dt.getUTCDay() };
};

// ── Kolon tanımı (Excel sırası) ───────────────────────────────────────
// auto:true  → sistem türevi (modpos / hesaplanan) → soluk ton
// auto:false → insan girdisi (Faz A grid'inde okunur, G10 düzenler)
// cash:true  → nakit zinciri (açılış/devir) → sol kenar vurgusu
// signed:true→ negatif olabilir (bahşiş J) → eksi kırmızı
// fark:true  → mutabakat farkı → işaret bazlı renk: 0=yeşil (uyumlu) / +=turuncu (fazla) / −=kırmızı (eksik)
const KOLONLAR = [
    { key: 'kasa_acilis', baslik: 'Kasa Açılış', auto: false, cash: true },
    { key: 'kasa_nakit', baslik: 'Kasada Bulunan', auto: false },
    { key: 'modpos_nakit', baslik: 'Modpos Nakit', auto: true },
    {
        key: 'nakit_fark', baslik: 'Nakit Fark', auto: true, fark: true,
        ipucu: 'Kasada Bulunan − Beklenen Nakit. 0 = uyumlu (yeşil), + fazla (turuncu): beklenenden çok nakit, − eksik (kırmızı): nakit açığı.',
    },
    { key: 'giderler', baslik: 'Giderler', auto: false },
    { key: 'teslim_edilen_nakit', baslik: 'Teslim Edilen', auto: true },
    { key: 'kasa_devri', baslik: 'Kasa Devri', auto: false, cash: true },
    { key: 'modpos_kredi_kart', baslik: 'Modpos Kart', auto: true },
    { key: 'kredi_karti_toplami', baslik: 'Σ Terminal (Q)', auto: true },
    { key: 'kart_bahsis', baslik: 'Bahşiş (J)', auto: true, signed: true },
    { key: 'yemek_karti_toplami', baslik: 'Yemek Kartı (Z)', auto: true },
    { key: 'yemek_karti_kdv', baslik: 'KDV', auto: true },
];

// ── Stiller ───────────────────────────────────────────────────────────
const pageStyle = {
    padding: '4px 2px 40px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: MANUAL_TONE,
};

const controlBarStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 12,
};

const selectStyle = {
    padding: '6px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'inherit',
    background: '#fff',
    color: MANUAL_TONE,
    cursor: 'pointer',
};

const navBtnStyle = {
    width: 30,
    height: 30,
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    background: '#fff',
    color: '#475569',
    cursor: 'pointer',
    fontSize: 15,
    lineHeight: 1,
    fontFamily: 'inherit',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: 12.5,
    fontVariantNumeric: 'tabular-nums',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    overflow: 'hidden',
};

const numTd = {
    padding: '4px 10px',
    borderBottom: '1px solid #eef2f7',
    borderLeft: '1px solid #f1f5f9',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
};

const legendChip = (tone) => ({
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: 2,
    background: tone,
    marginRight: 5,
    verticalAlign: 'middle',
});

// ── Ana bileşen ───────────────────────────────────────────────────────
export default function KasaDefterPage() {
    const { user } = useAuth();
    const canView = !user || hasPermission(user.role, PERMISSIONS.VIEW_KASA_KAPANIS, user.effectivePermissions);
    const canManage = !user || hasPermission(user.role, PERMISSIONS.MANAGE_KASA_KAPANIS, user.effectivePermissions);

    const toast = useToast();
    const now = new Date();
    const [lokasyonlar, setLokasyonlar] = useState([]);
    // Filtre state'i URL'e ait (konvansiyon): geri/refresh'te korunur, link paylaşılabilir.
    // useUrlState daima STRING döndürür → sayısal kullanım için ayNum/yilNum türetilir.
    const [lokasyonId, setLokasyonId] = useUrlState('lokasyon', '');
    const [yil, setYil] = useUrlState('yil', String(now.getFullYear()));
    const [ay, setAy] = useUrlState('ay', String(now.getMonth() + 1)); // 1-12 (string)
    const ayNum = Number(ay) || (now.getMonth() + 1);
    const yilNum = Number(yil) || now.getFullYear();
    const [grid, setGrid] = useState(null); // { gunler, ozet }
    const [seciliTarih, setSeciliTarih] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Gün detay editörü (sağ panel) — tıklanan günün tarihini tutar.
    const [editorTarih, setEditorTarih] = useState(null);

    // Auto-select stale-closure önlemek için güncel lokasyonId ref'i (useUrlState
    // setValue fonksiyonel updater desteklemez → değeri ref'ten okuyup string yaz).
    const lokasyonIdRef = useRef(lokasyonId);
    lokasyonIdRef.current = lokasyonId;

    // Lokasyonları bir kere yükle — Anayasa Madde 1 (DB'den, aktif olanlar)
    useEffect(() => {
        let aktif = true;
        fetch(`${API_URL}/api/lokasyonlar`, { headers: authHeaders() })
            .then((r) => (r.ok ? r.json() : []))
            .then((d) => {
                if (!aktif) return;
                const liste = Array.isArray(d) ? d.filter((l) => l.aktif !== false) : [];
                setLokasyonlar(liste);
                // İlk lokasyonu otomatik seç (URL'de seçim yoksa; kullanıcı boş ekran görmesin)
                if (!lokasyonIdRef.current && liste[0]) setLokasyonId(String(liste[0].id));
            })
            .catch(() => aktif && setLokasyonlar([]));
        return () => {
            aktif = false;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const yukle = useCallback(async () => {
        if (!lokasyonId) {
            setGrid(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const url = `${API_URL}/api/kasa-defter/ay?lokasyon_id=${lokasyonId}&yil=${yilNum}&ay=${ayNum}`;
            const r = await fetch(url, { headers: authHeaders() });
            if (r.ok) {
                const data = await r.json();
                setGrid({
                    gunler: Array.isArray(data.gunler) ? data.gunler : [],
                    ozet: data.ozet || {},
                });
            } else {
                const txt = await r.text().catch(() => '');
                setError(`Defter yüklenemedi (${r.status}) ${txt.slice(0, 200)}`);
                setGrid(null);
            }
        } catch (e) {
            setError(`Ağ hatası: ${e.message || e}`);
            setGrid(null);
        } finally {
            setLoading(false);
        }
    }, [lokasyonId, yilNum, ayNum]);

    useEffect(() => {
        yukle();
    }, [yukle]);

    const lokasyonAd = useMemo(() => {
        const l = lokasyonlar.find((x) => String(x.id) === String(lokasyonId));
        return l ? l.ad : '';
    }, [lokasyonlar, lokasyonId]);

    const oncekiAy = () => {
        if (ayNum === 1) { setAy('12'); setYil(String(yilNum - 1)); } else { setAy(String(ayNum - 1)); }
    };
    const sonrakiAy = () => {
        if (ayNum === 12) { setAy('1'); setYil(String(yilNum + 1)); } else { setAy(String(ayNum + 1)); }
    };

    const bugunIso = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, []);

    // Yıl seçimi: cari yıl ± 3 (takvim gerçeği — hardcoded master data değil)
    const yilSecenekleri = useMemo(() => {
        const cy = now.getFullYear();
        const set = new Set([cy - 3, cy - 2, cy - 1, cy, cy + 1, yilNum]);
        return Array.from(set).sort((a, b) => a - b);
    }, [yilNum]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!canView) {
        return (
            <div style={{ ...pageStyle, color: '#64748b', textAlign: 'center', marginTop: 60 }}>
                Kasa defterini görüntüleme yetkiniz yok.
            </div>
        );
    }

    const gunler = grid?.gunler || [];
    const ozet = grid?.ozet || {};
    const doluGunSayisi = gunler.filter((g) => g.kapanis_id !== null && g.kapanis_id !== undefined).length;

    return (
        <div style={pageStyle}>
            {/* Başlık + kontrol çubuğu */}
            <div style={controlBarStyle}>
                <h2 style={{ margin: '0 12px 0 0', fontSize: 18, fontWeight: 700, color: '#222' }}>
                    📒 Kasa Defteri
                </h2>

                <select
                    value={lokasyonId}
                    onChange={(e) => setLokasyonId(e.target.value)}
                    style={selectStyle}
                    aria-label="Lokasyon"
                >
                    {lokasyonlar.length === 0 && <option value="">Lokasyon yok</option>}
                    {lokasyonlar.map((l) => (
                        <option key={l.id} value={l.id}>{l.ad}</option>
                    ))}
                </select>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button type="button" onClick={oncekiAy} style={navBtnStyle} title="Önceki ay">‹</button>
                    <select
                        value={ay}
                        onChange={(e) => setAy(e.target.value)}
                        style={selectStyle}
                        aria-label="Ay"
                    >
                        {AYLAR.map((adi, i) => (
                            <option key={i} value={i + 1}>{adi}</option>
                        ))}
                    </select>
                    <select
                        value={yil}
                        onChange={(e) => setYil(e.target.value)}
                        style={selectStyle}
                        aria-label="Yıl"
                    >
                        {yilSecenekleri.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                    <button type="button" onClick={sonrakiAy} style={navBtnStyle} title="Sonraki ay">›</button>
                </div>

                {loading && <span style={{ color: '#94a3b8', fontSize: 12 }}>Yükleniyor…</span>}

                {/* Legend — auto vs manuel ayrımı (komik olmamasının anahtarı) */}
                <div style={{
                    marginLeft: 'auto', fontSize: 11.5, color: '#64748b',
                    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                }}>
                    <span><span style={legendChip(MANUAL_TONE)} />Elle girilen</span>
                    <span><span style={legendChip(AUTO_TONE)} />Modpos / otomatik</span>
                    <span><span style={legendChip(CASH_ACCENT)} />Nakit zinciri</span>
                    <span
                        title="Nakit Fark = Kasada Bulunan − Beklenen Nakit"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, paddingLeft: 10, borderLeft: '1px solid #e2e8f0' }}
                    >
                        <span style={{ color: '#94a3b8' }}>Fark:</span>
                        <span style={{ color: FARK_OK_TONE, fontWeight: 600 }}>✓ uyumlu</span>
                        <span style={{ color: FARK_FAZLA_TONE, fontWeight: 600 }}>▲ fazla</span>
                        <span style={{ color: FARK_EKSIK_TONE, fontWeight: 600 }}>▼ eksik</span>
                    </span>
                </div>
            </div>

            {error && (
                <div style={{
                    color: BRAND, fontSize: 12.5, marginBottom: 10,
                    padding: 8, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
                }}>
                    {error}
                </div>
            )}

            {/* Ay özeti — sticky üst bar (ozet read-model) */}
            <OzetBar ozet={ozet} ay={ayNum} yil={yilNum} lokasyonAd={lokasyonAd} doluGun={doluGunSayisi} />

            {/* Defter grid'i */}
            <div style={{ overflowX: 'auto', marginTop: 10 }}>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={{
                                position: 'sticky', left: 0, zIndex: 3,
                                background: '#f1f5f9', textAlign: 'left',
                                padding: '8px 10px', borderBottom: '2px solid #cbd5e1',
                                fontWeight: 700, color: '#334155', fontSize: 11.5,
                                whiteSpace: 'nowrap', minWidth: 96,
                            }}>
                                Tarih
                            </th>
                            {KOLONLAR.map((c) => (
                                <th
                                    key={c.key}
                                    style={{
                                        textAlign: 'right',
                                        padding: '8px 10px',
                                        borderBottom: '2px solid #cbd5e1',
                                        borderLeft: c.cash ? `2px solid ${CASH_ACCENT}` : '1px solid #e2e8f0',
                                        background: c.auto ? '#f8fafc' : '#eef2f7',
                                        color: c.auto ? '#64748b' : '#334155',
                                        fontWeight: 700,
                                        fontSize: 11.5,
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={c.ipucu || (c.auto ? 'Modpos / otomatik hesaplanan' : 'Elle girilen (gün detayında düzenlenir)')}
                                >
                                    {c.baslik}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {gunler.length === 0 && !loading && (
                            <tr>
                                <td colSpan={KOLONLAR.length + 1} style={{
                                    padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13,
                                }}>
                                    {lokasyonId ? 'Bu ay için veri yok.' : 'Bir lokasyon seçin.'}
                                </td>
                            </tr>
                        )}
                        {gunler.map((g) => (
                            <GunSatiri
                                key={g.tarih}
                                gun={g}
                                bugun={g.tarih === bugunIso}
                                secili={g.tarih === seciliTarih}
                                onSelect={() => { setSeciliTarih(g.tarih); setEditorTarih(g.tarih); }}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: 10, fontSize: 11.5, color: '#94a3b8' }}>
                Soluk satırlar henüz kapanışı açılmamış günlerdir. Bir güne tıklayıp
                <strong style={{ color: '#64748b' }}> “Gün aç”</strong> ile o günün kasa girişini
                yapın: terminal Z raporları, teslim edilen nakit, devir ve nakit harcama girilir;
                Modpos değerleri otomatik gelir, sistem teslim/bahşiş/KDV'yi hesaplar.
            </div>

            {/* Gün detay editörü (sağ kayan panel) */}
            {editorTarih && (
                <GunDetayEditor
                    tarih={editorTarih}
                    lokasyonId={lokasyonId}
                    lokasyonAd={lokasyonAd}
                    canManage={canManage}
                    onClose={() => setEditorTarih(null)}
                    onSaved={() => {
                        toast.success('Kaydedildi');
                        yukle(); // ayı yeniden çek → grid türetilmiş kolonlarla tazelenir
                    }}
                />
            )}
        </div>
    );
}

// ── Gün detay editörü (sağ kayan panel) ───────────────────────────────
// Tıklanan gün için GET /api/kasa-defter/gun ile açar (yoksa TASLAK yaratır),
// manuel girdileri toplar, canlı Q/J/Z/teslim/KDV önizler (hesapla_gun aynası),
// PUT ile kaydeder. TAMAMLANDI → tüm girişler salt-okunur.
const numToStr = (n) => {
    // Backend float → input string. tr-TR ondalık: nokta yerine virgül kullan ki
    // strToNum ile birebir round-trip olsun (binlik gruplama YOK — re-parse güvenli).
    // 0/NaN → boş input (auto-fill 0'ı temiz görünür, kullanıcı sıfırdan yazar).
    if (n === null || n === undefined) return '';
    const v = Number(n);
    if (!v) return '';
    return String(v).replace('.', ',');
};
const strToNum = (s) => {
    // tr-TR girdi: '21.919,50' (binlik nokta + ondalık virgül) ya da '21919,50'.
    // Binlik noktaları at, ondalık virgülü noktaya çevir. Boş/eksik → 0.
    if (s === null || s === undefined) return 0;
    const v = parseFloat(String(s).replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(v) ? v : 0;
};

function GunDetayEditor({ tarih, lokasyonId, lokasyonAd, canManage, onClose, onSaved }) {
    const toast = useToast();
    const [gun, setGun] = useState(null);     // backend gün objesi (id, durum, modpos…)
    const [loading, setLoading] = useState(true);
    const [hata, setHata] = useState(null);
    const [kaydediliyor, setKaydediliyor] = useState(false);
    const kapatRef = useRef(null);          // açılışta odaklanılacak Kapat butonu
    const onCloseRef = useRef(onClose);     // Esc handler daima güncel onClose'u çağırsın
    onCloseRef.current = onClose;

    // Erişilebilirlik: Esc ile kapat + açılışta odağı panele taşı + kapanışta
    // odağı tetikleyen satıra geri ver (klavye kullanıcısı tabloda kaybolmasın).
    useEffect(() => {
        const oncekiOdak = document.activeElement;
        const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current?.(); };
        document.addEventListener('keydown', onKey);
        const t = setTimeout(() => kapatRef.current?.focus(), 0);
        return () => {
            document.removeEventListener('keydown', onKey);
            clearTimeout(t);
            if (oncekiOdak instanceof HTMLElement) oncekiOdak.focus();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Düzenlenebilir yerel durum (backend yüklendikten sonra doldurulur) ──
    const [terminalZ, setTerminalZ] = useState([]);   // [{ pos_terminal_id, pos_terminal_ad, deger:str }]
    const [yk, setYk] = useState([]);                 // [{ odeme_komisyon_id, kod, ad, modpos }]
    const [teslimEdilen, setTeslimEdilen] = useState('');
    const [kasaDevri, setKasaDevri] = useState('');
    const [kasaAcilis, setKasaAcilis] = useState(''); // D — normalde dünkü devirden auto; ilk gün elle
    const [nakitHarcama, setNakitHarcama] = useState('');                  // F — tek toplam nakit harcama
    const [nakitHarcamaAciklama, setNakitHarcamaAciklama] = useState('');  // serbest metin (salt-bilgi)
    const [notlar, setNotlar] = useState('');

    const tamamlandi = gun?.durum === 'TAMAMLANDI';
    const readOnly = tamamlandi || !canManage;

    // Günü aç/getir (idempotent — yoksa TASLAK yaratır + auto-fill).
    useEffect(() => {
        if (!tarih || !lokasyonId) return;
        let aktif = true;
        setLoading(true);
        setHata(null);
        const url = `${API_URL}/api/kasa-defter/gun?lokasyon_id=${lokasyonId}&tarih=${tarih}`;
        fetch(url, { headers: authHeaders() })
            .then(async (r) => {
                if (!r.ok) {
                    const txt = await r.text().catch(() => '');
                    throw new Error(`Gün açılamadı (${r.status}) ${txt.slice(0, 180)}`);
                }
                return r.json();
            })
            .then((d) => {
                if (!aktif) return;
                setGun(d);
                setTerminalZ((d.kart_detaylar || []).map((kd) => ({
                    pos_terminal_id: kd.pos_terminal_id,
                    pos_terminal_ad: kd.pos_terminal_ad || `Terminal ${kd.pos_terminal_id}`,
                    deger: numToStr(kd.z_raporu_tutar),
                })));
                setYk((d.yk_detaylar || []).map((y) => ({
                    odeme_komisyon_id: y.odeme_komisyon_id,
                    kod: y.kod,
                    ad: y.ad,
                    modpos: Number(y.modpos_snapshot) || 0,
                })));
                setTeslimEdilen(numToStr(d.teslim_edilen_nakit));
                setKasaDevri(numToStr(d.kasa_devri));
                setKasaAcilis(numToStr(d.kasa_acilis));
                setNakitHarcama(numToStr(d.nakit_harcama));
                setNakitHarcamaAciklama(d.nakit_harcama_aciklama || '');
                setNotlar(d.genel_notlar || '');
            })
            .catch((e) => { if (aktif) setHata(e.message || String(e)); })
            .finally(() => { if (aktif) setLoading(false); });
        return () => { aktif = false; };
    }, [tarih, lokasyonId]);

    // ── Canlı önizleme (backend otorite, bu UX). Kart/YK: hesapla_gun aynası;
    // nakit: tutarlı model (sayım = teslim+devir; fark = sayım − beklenen). ──
    const onizleme = useMemo(() => {
        const acilis = strToNum(kasaAcilis);                   // D (elle girilebilir; default dünkü devir)
        const modposKart = Number(gun?.modpos_kredi_kart) || 0; // I (auto-fill)
        const modposNakit = Number(gun?.modpos_nakit_snapshot) || 0; // modpos NAKİT (gün-aç snapshot)
        const q = terminalZ.reduce((s, t) => s + strToNum(t.deger), 0);        // Q = Σ terminal Z
        const j = q - modposKart;                                              // J = Q − modpos (negatif olabilir)
        const z = yk.reduce((s, y) => s + (Number(y.modpos) || 0), 0);         // Z = Σ sağlayıcı modpos
        const giderToplam = strToNum(nakitHarcama);                            // F = tek toplam nakit harcama
        const teslim = strToNum(teslimEdilen);                                 // T — teslim edilen (manuel)
        const devri = strToNum(kasaDevri);                                     // E — yarına bırakılan
        const sayim = teslim + devri;                                          // kasada bulunan (sayım)
        const kdv = z ? (z / 1.1) * 0.10 : 0;                                  // AA
        // Beklenen kasa nakdi = açılış + modpos nakit (satış) − giderler (nakit ödenen).
        // Kart bahşişi o gün kasadan ÇIKMAZ (ertesi gün kart hesabıyla gelir) → hariç.
        const beklenenNakit = acilis + modposNakit - giderToplam;
        const nakitFark = sayim - beklenenNakit;                               // (teslim+devir) − beklenen (0 → temiz)
        return { acilis, modposKart, modposNakit, q, j, z, giderToplam, teslim, sayim, kdv, beklenenNakit, nakitFark };
    }, [gun, terminalZ, yk, nakitHarcama, teslimEdilen, kasaDevri, kasaAcilis]);

    const setTerminalDeger = (idx, val) => {
        setTerminalZ((prev) => prev.map((t, i) => (i === idx ? { ...t, deger: val } : t)));
    };

    const kaydet = async () => {
        if (!gun?.id || readOnly) return;
        setKaydediliyor(true);
        setHata(null);
        const body = {
            teslim_edilen_nakit: strToNum(teslimEdilen),
            kasa_devri: strToNum(kasaDevri),
            kasa_acilis: strToNum(kasaAcilis),
            genel_notlar: notlar || null,
            kart_detaylar: terminalZ.map((t) => ({
                pos_terminal_id: t.pos_terminal_id,
                z_raporu_tutar: strToNum(t.deger),
            })),
            yk_detaylar: yk.map((y) => ({
                odeme_komisyon_id: y.odeme_komisyon_id,
                beyan_tutar: 0,
                modpos_snapshot: Number(y.modpos) || 0,
            })),
            nakit_harcama: strToNum(nakitHarcama),
            nakit_harcama_aciklama: nakitHarcamaAciklama || null,
        };
        try {
            const r = await fetch(`${API_URL}/api/kasa-defter/gun/${gun.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(body),
            });
            if (!r.ok) {
                const txt = await r.text().catch(() => '');
                throw new Error(`Kaydedilemedi (${r.status}) ${txt.slice(0, 200)}`);
            }
            const guncel = await r.json();
            setGun(guncel); // türetilen alanlar (Q/J/teslim/KDV) backend'den yenilenir
            if (onSaved) onSaved();
        } catch (e) {
            // İşlem sonucu → sağ-üst toast (panel scroll'dayken inline hata görünmeyebilir).
            toast.error(e.message || String(e));
        } finally {
            setKaydediliyor(false);
        }
    };

    // Enter → bir sonraki terminal input'a in (hızlı Z girişi).
    const terminalKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const next = e.target.closest('[data-terminal-row]')?.nextElementSibling;
            const inp = next?.querySelector('input');
            if (inp) inp.focus();
        }
    };

    const { gun: gunNo, haftaGunu } = isoToParts(tarih);

    return (
        <>
            {/* Karartma (backdrop) — tıklayınca kapat */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0, zIndex: 1000,
                    background: 'rgba(15,23,42,0.32)',
                }}
            />
            {/* Sağ panel */}
            <div
                role="dialog"
                aria-label="Gün detayı"
                style={{
                    position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1001,
                    width: 'min(480px, 96vw)', background: '#fff',
                    boxShadow: '-8px 0 28px rgba(15,23,42,0.18)',
                    display: 'flex', flexDirection: 'column',
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    color: MANUAL_TONE,
                }}
            >
                {/* Başlık */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '14px 18px', borderBottom: '1px solid #e2e8f0',
                    background: 'linear-gradient(180deg,#fff,#fbfbfc)',
                }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        background: '#fdecea', color: BRAND,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 800, lineHeight: 1,
                    }}>
                        <span style={{ fontSize: 16 }}>{gunNo}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, marginTop: 1 }}>{HAFTA_GUNLERI[haftaGunu]}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#222' }}>
                            {tarihUzun(tarih)}
                        </div>
                        <div style={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {lokasyonAd || `Lokasyon #${lokasyonId}`}
                            {gun?.durum && (
                                <DurumRozeti durum={gun.durum} />
                            )}
                        </div>
                    </div>
                    <button
                        ref={kapatRef}
                        type="button"
                        onClick={onClose}
                        title="Kapat (Esc)"
                        aria-label="Kapat"
                        style={{
                            width: 30, height: 30, border: '1px solid #e2e8f0', borderRadius: 6,
                            background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 16, lineHeight: 1,
                        }}
                    >×</button>
                </div>

                {/* Gövde (kaydırılabilir) */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px 18px' }}>
                    {loading && (
                        <div style={{ color: '#94a3b8', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                            Gün açılıyor…
                        </div>
                    )}

                    {hata && (
                        <div style={{
                            color: BRAND, fontSize: 12.5, marginBottom: 12, padding: 9,
                            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
                        }}>
                            {hata}
                        </div>
                    )}

                    {!loading && gun && (
                        <>
                            {tamamlandi && (
                                <div style={{
                                    fontSize: 12, color: '#92400e', background: '#fffbeb',
                                    border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 14,
                                }}>
                                    Bu gün <strong>tamamlanmış</strong> — salt okunur. Düzenlemek için
                                    önce “Yeniden Aç” yapılmalıdır.
                                </div>
                            )}
                            {!canManage && !tamamlandi && (
                                <div style={{
                                    fontSize: 12, color: '#475569', background: '#f8fafc',
                                    border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', marginBottom: 14,
                                }}>
                                    Düzenleme yetkiniz yok — yalnızca görüntülüyorsunuz.
                                </div>
                            )}

                            {/* ── Auto / salt-okunur (sistem türevi) ── */}
                            <Bolum baslik="Otomatik (Modpos)" alt="Sistem doldurdu — değiştirilemez">
                                <OkunurSatir etiket="Modpos Kredi Kart" deger={onizleme.modposKart} ipucu="Modpos kredi kartı toplamı (I)" />
                            </Bolum>

                            {/* ── Terminal Z (asıl manuel iş — odak) ── */}
                            <Bolum
                                baslik="Banka Terminali Z Raporları"
                                alt="Her POS cihazının gün sonu Z tutarını gir"
                                vurgu
                            >
                                {terminalZ.length === 0 && (
                                    <div style={{ fontSize: 12, color: '#94a3b8', padding: '4px 0' }}>
                                        Bu lokasyonda tanımlı aktif POS terminali yok.
                                    </div>
                                )}
                                {terminalZ.map((t, idx) => (
                                    <div
                                        key={t.pos_terminal_id}
                                        data-terminal-row
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}
                                    >
                                        <label style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>
                                            {t.pos_terminal_ad}
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={t.deger}
                                            disabled={readOnly}
                                            onChange={(e) => setTerminalDeger(idx, e.target.value)}
                                            onKeyDown={terminalKeyDown}
                                            placeholder="0,00"
                                            style={sayiInput(readOnly)}
                                        />
                                    </div>
                                ))}
                                {/* Σ Terminal = Q (canlı) */}
                                <div style={toplamSatir}>
                                    <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>Σ Terminal (Q)</span>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>
                                        {formatTl(onizleme.q)} <span style={{ fontSize: 11, color: '#94a3b8' }}>TL</span>
                                    </span>
                                </div>
                            </Bolum>

                            {/* ── Nakit (teslim + devir = sayım) — modpos ile mutabakat ── */}
                            <Bolum baslik="Nakit" alt="Kasada bulunan (teslim + devir), beklenen (açılış + modpos − gider) ile karşılaştırılır">
                                <AlanSatir
                                    etiket="Kasa Açılış"
                                    ipucu="Sabah kasada olan nakit. Normalde dünkü 'Kasa Devri'nden otomatik gelir; ilk gün veya düzeltme için elle gir."
                                    value={kasaAcilis}
                                    onChange={setKasaAcilis}
                                    disabled={readOnly}
                                    cash
                                />
                                {/* Açılış → Modpos Nakit → − Giderler → = Beklenen → Teslim → Devir → Kasada Bulunan → Fark */}
                                <OkunurSatir
                                    etiket="Modpos Nakit"
                                    deger={onizleme.modposNakit}
                                    ipucu="Modpos'un kaydettiği NAKİT satış toplamı (gün açılışında alındı)"
                                />
                                <OkunurSatir
                                    etiket="− Giderler"
                                    deger={onizleme.giderToplam}
                                    ipucu="Kasadan nakit ödenen gün içi harcamalar (beklenen nakdi düşürür)"
                                />
                                <OkunurSatir
                                    etiket="= Beklenen Nakit"
                                    deger={onizleme.beklenenNakit}
                                    ipucu="Açılış + Modpos Nakit − Giderler. Kasada olması beklenen tutar."
                                />
                                <AlanSatir
                                    etiket="Teslim Edilen"
                                    ipucu="İşletmeye/sahibe teslim ettiğin nakit."
                                    value={teslimEdilen}
                                    onChange={setTeslimEdilen}
                                    disabled={readOnly}
                                    cash
                                />
                                <AlanSatir
                                    etiket="Kasa Devri"
                                    ipucu="Yarına bıraktığın nakit (yarının açılışı olur)."
                                    value={kasaDevri}
                                    onChange={setKasaDevri}
                                    disabled={readOnly}
                                    cash
                                />
                                <OkunurSatir
                                    etiket="Kasada Bulunan (Teslim+Devir)"
                                    deger={onizleme.sayim}
                                    ipucu="Teslim edilen + kasa devri = fiilen kasada bulunan (sayım)."
                                />
                                <FarkSatir
                                    fark={onizleme.nakitFark}
                                    bos={strToNum(teslimEdilen) === 0 && strToNum(kasaDevri) === 0}
                                />
                            </Bolum>

                            {/* ── Nakit Harcama (tek toplam) ── */}
                            <Bolum
                                baslik="Nakit Harcama"
                                alt="Kasadan yapılan toplam nakit harcama (yalnız nakit mutabakatı; detaylı gider Gider Girişi sayfasından)"
                            >
                                <AlanSatir
                                    etiket="Toplam Nakit Harcama"
                                    ipucu="Gün içinde kasadan nakit ödenen toplam tutar. Beklenen nakdi düşürür. Detaylı/kategorili gider kaydı buraya değil, Gider Girişi sayfasına girilir — bu tutar başka bir yere yansımaz."
                                    value={nakitHarcama}
                                    onChange={setNakitHarcama}
                                    disabled={readOnly}
                                    cash
                                />
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>
                                        Açıklama (opsiyonel)
                                    </div>
                                    <textarea
                                        value={nakitHarcamaAciklama}
                                        disabled={readOnly}
                                        onChange={(e) => setNakitHarcamaAciklama(e.target.value)}
                                        placeholder="Örn. çay-kahve, su, kırtasiye…"
                                        rows={2}
                                        style={{
                                            width: '100%', boxSizing: 'border-box', resize: 'vertical',
                                            border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px',
                                            fontSize: 13, fontFamily: 'inherit',
                                            background: readOnly ? '#f8fafc' : '#fff', color: '#0f172a',
                                        }}
                                    />
                                </div>
                            </Bolum>

                            {/* ── Yemek kartı (modpos doğrulama — salt-okunur) ── */}
                            {yk.length > 0 && (
                                <Bolum baslik="Yemek Kartı (Modpos)" alt="Sağlayıcı tutarları Modpos'tan gelir">
                                    {yk.map((y) => (
                                        <div key={y.odeme_komisyon_id} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '3px 0', fontSize: 12.5,
                                        }}>
                                            <span style={{ color: '#475569' }}>{y.ad || y.kod}</span>
                                            <span style={{ color: AUTO_TONE, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                                {formatTl(y.modpos)}
                                            </span>
                                        </div>
                                    ))}
                                    <div style={toplamSatir}>
                                        <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>Σ Yemek Kartı (Z)</span>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>
                                            {formatTl(onizleme.z)} <span style={{ fontSize: 11, color: '#94a3b8' }}>TL</span>
                                        </span>
                                    </div>
                                </Bolum>
                            )}

                            {/* ── Genel notlar ── */}
                            <Bolum baslik="Genel Notlar" alt="İsteğe bağlı">
                                <textarea
                                    value={notlar}
                                    disabled={readOnly}
                                    onChange={(e) => setNotlar(e.target.value)}
                                    placeholder="Gün ile ilgili not…"
                                    rows={2}
                                    style={{
                                        width: '100%', boxSizing: 'border-box', padding: '7px 9px',
                                        border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5,
                                        fontFamily: 'inherit', resize: 'vertical',
                                        background: readOnly ? '#f8fafc' : '#fff', color: MANUAL_TONE,
                                    }}
                                />
                            </Bolum>
                        </>
                    )}
                </div>

                {/* Alt bar — canlı hesaplama özeti + aksiyonlar */}
                {!loading && gun && (
                    <div style={{ borderTop: '1px solid #e2e8f0', background: '#fbfbfc' }}>
                        {/* Canlı hesaplama önizleme */}
                        <div style={{ padding: '10px 18px 4px', display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                            <HesapKart
                                etiket="Bahşiş (J)"
                                deger={onizleme.j}
                                signed
                                bos={onizleme.q === 0}
                                ipucu="Kredi kartı bahşişi (Σ Terminal − Modpos). Terminal Z girilince hesaplanır."
                            />
                            <HesapKart etiket="Yemek K. KDV" deger={onizleme.kdv} ipucu="Z ÷ 1,10 × %10" />
                        </div>
                        {/* Aksiyon butonları */}
                        <div style={{ display: 'flex', gap: 10, padding: '8px 18px 14px' }}>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    flex: '0 0 auto', padding: '9px 16px', border: '1px solid #cbd5e1',
                                    borderRadius: 7, background: '#fff', color: '#475569', cursor: 'pointer',
                                    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                                }}
                            >
                                {readOnly ? 'Kapat' : 'Vazgeç'}
                            </button>
                            {!readOnly && (
                                <button
                                    type="button"
                                    onClick={kaydet}
                                    disabled={kaydediliyor}
                                    style={{
                                        flex: 1, padding: '9px 16px', border: 'none', borderRadius: 7,
                                        background: kaydediliyor ? '#e5a3a3' : BRAND, color: '#fff',
                                        cursor: kaydediliyor ? 'default' : 'pointer',
                                        fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                                    }}
                                >
                                    {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

// Uzun tarih: '2026-05-01' → '1 Mayıs 2026 Cuma'
function tarihUzun(iso) {
    const [y, m, d] = String(iso).split('-').map(Number);
    const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
    const tamGun = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'][dt.getUTCDay()];
    return `${d} ${AYLAR[(m || 1) - 1]} ${y} ${tamGun}`;
}

function DurumRozeti({ durum }) {
    const tamam = durum === 'TAMAMLANDI';
    return (
        <span style={{
            marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 8,
            background: tamam ? '#dcfce7' : '#fef3c7',
            color: tamam ? '#166534' : '#92400e',
            border: `1px solid ${tamam ? '#bbf7d0' : '#fde68a'}`,
        }}>
            {tamam ? 'Tamamlandı' : 'Taslak'}
        </span>
    );
}

// Bölüm kabı (başlık + alt açıklama + opsiyonel aksiyon/vurgu)
function Bolum({ baslik, alt, aksiyon, vurgu, children }) {
    return (
        <div style={{
            marginBottom: 16,
            border: vurgu ? `1px solid ${BRAND}33` : '1px solid #eef2f7',
            borderRadius: 8,
            background: vurgu ? '#fffafa' : '#fff',
            padding: '10px 12px 12px',
        }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: vurgu ? BRAND : '#334155' }}>{baslik}</div>
                    {alt && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{alt}</div>}
                </div>
                {aksiyon}
            </div>
            {children}
        </div>
    );
}

// Salt-okunur değer satırı (gri ton)
function OkunurSatir({ etiket, deger, ipucu }) {
    return (
        <div
            title={ipucu}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}
        >
            <span style={{ fontSize: 12.5, color: '#64748b' }}>{etiket}</span>
            <span style={{ fontSize: 13, color: AUTO_TONE, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {formatTl(deger)}
            </span>
        </div>
    );
}

// Nakit mutabakat farkı satırı (Kasada Bulunan − Beklenen). İşaret bazlı 3 renk:
//   0  → yeşil "uyumlu"     (sayım = beklenen)
//   +  → turuncu "fazla"    (kasada beklenenden çok nakit)
//   −  → kırmızı "eksik"    (kasada nakit açığı — en kritik)
// bos:true → teslim/devir henüz girilmedi → fark anlamsız, "—" göster (nötr).
function FarkSatir({ fark, bos }) {
    const v = Number(fark) || 0;
    const uyumlu = !bos && Math.abs(v) < 0.005; // kuruş altı → uyumlu say
    const fazla = !bos && !uyumlu && v > 0;
    const eksik = !bos && !uyumlu && v < 0;

    let renk = '#94a3b8', bg = '#f8fafc', bd = '#e2e8f0';   // bos → nötr gri
    if (uyumlu) { renk = FARK_OK_TONE; bg = '#f0fdf4'; bd = '#bbf7d0'; }
    else if (fazla) { renk = FARK_FAZLA_TONE; bg = '#fff7ed'; bd = '#fed7aa'; }
    else if (eksik) { renk = FARK_EKSIK_TONE; bg = '#fef2f2'; bd = '#fecaca'; }

    let etiket = 'Nakit Fark';
    if (uyumlu) etiket = '✓ Nakit Fark (uyumlu)';
    else if (fazla) etiket = '▲ Nakit Fark (fazla)';
    else if (eksik) etiket = '▼ Nakit Fark (eksik)';

    return (
        <div
            title="Kasada Bulunan (Teslim + Devir) − Beklenen Nakit. 0 = uyumlu (yeşil); + fazla (turuncu): beklenenden çok nakit; − eksik (kırmızı): nakit açığı → incelenmeli. Teslim veya devir girilince hesaplanır."
            style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '4px 8px', marginBottom: 7, borderRadius: 6,
                background: bg,
                border: `1px solid ${bd}`,
            }}
        >
            <span style={{ fontSize: 12, color: renk, fontWeight: 600 }}>
                {etiket}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: renk, fontVariantNumeric: 'tabular-nums' }}>
                {bos ? '—' : (
                    <>
                        {`${v > 0 ? '+' : v < 0 ? '-' : ''}${formatTl(Math.abs(v))}`}
                        <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}> TL</span>
                    </>
                )}
            </span>
        </div>
    );
}

// Düzenlenebilir tek alan (etiket + sayı input)
function AlanSatir({ etiket, ipucu, value, onChange, disabled, cash }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }} title={ipucu}>
            <label style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>
                {cash && <span style={{ color: CASH_ACCENT, marginRight: 4 }}>●</span>}
                {etiket}
            </label>
            <input
                type="text"
                inputMode="decimal"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                placeholder="0,00"
                style={sayiInput(disabled)}
            />
        </div>
    );
}

// Canlı hesaplama kartı (alt bar) — signed=true ise negatif kırmızı
// bos:true → gereken manuel girdi henüz yok (örn. terminal Z girilmeden Bahşiş
// anlamsız) → değer yerine "—" göster.
function HesapKart({ etiket, deger, signed, ipucu, bos }) {
    const v = Number(deger) || 0;
    const neg = signed && v < 0 && !bos;
    return (
        <div title={ipucu} style={{ flex: 1, minWidth: 110, padding: '4px 8px 6px' }}>
            <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>{etiket}</div>
            <div style={{
                fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                color: bos ? '#cbd5e1' : (neg ? BRAND : '#1f2937'), whiteSpace: 'nowrap',
            }}>
                {bos ? '—' : (
                    <>
                        {`${v < 0 ? '-' : ''}${formatTl(Math.abs(v))}`}
                        <span style={{ fontSize: 10, fontWeight: 500, color: '#94a3b8' }}> TL</span>
                    </>
                )}
            </div>
        </div>
    );
}

const sayiInput = (disabled) => ({
    flex: '0 0 118px',
    width: 118,
    padding: '7px 9px',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'inherit',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    background: disabled ? '#f8fafc' : '#fff',
    color: disabled ? '#94a3b8' : MANUAL_TONE,
    boxSizing: 'border-box',
});

const toplamSatir = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0',
};

// ── Tek gün satırı ────────────────────────────────────────────────────
function GunSatiri({ gun, bugun, secili, onSelect }) {
    const [hover, setHover] = useState(false);
    const acik = gun.kapanis_id !== null && gun.kapanis_id !== undefined;
    const { gun: gunNo, haftaGunu } = isoToParts(gun.tarih);
    const haftaSonu = haftaGunu === 0 || haftaGunu === 6;

    // Satır arka planı: seçili > today > hover > hafta sonu > boş gün > normal
    let bg = '#fff';
    if (!acik) bg = '#fbfcfe';
    if (haftaSonu) bg = acik ? '#fafbfc' : '#f7f9fb';
    if (hover) bg = '#fff7f7';
    if (bugun) bg = '#fff5f5';
    if (secili) bg = '#fdecea';

    const tarihRengi = acik ? MANUAL_TONE : '#94a3b8';

    return (
        <tr
            onClick={onSelect}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{ cursor: 'pointer', background: bg, opacity: acik ? 1 : 0.78 }}
        >
            {/* Tarih (yapışkan ilk kolon) */}
            <td style={{
                position: 'sticky', left: 0, zIndex: 1,
                background: bg,
                padding: '4px 10px',
                borderBottom: '1px solid #eef2f7',
                borderLeft: bugun ? `3px solid ${BRAND}` : '3px solid transparent',
                whiteSpace: 'nowrap',
                fontWeight: 600,
                color: tarihRengi,
            }}>
                <span style={{ display: 'inline-block', minWidth: 22, textAlign: 'right' }}>{gunNo}</span>
                <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: haftaSonuRenk(haftaSonu, acik) }}>
                    {HAFTA_GUNLERI[haftaGunu]}
                </span>
                {!acik && (
                    <span style={{
                        marginLeft: 8,
                        fontSize: 10,
                        fontWeight: 600,
                        color: hover || secili ? BRAND : '#94a3b8',
                        border: `1px dashed ${hover || secili ? BRAND : '#cbd5e1'}`,
                        borderRadius: 8,
                        padding: '0 6px',
                        whiteSpace: 'nowrap',
                    }}>
                        + Gün aç
                    </span>
                )}
            </td>

            {/* Sayısal kolonlar */}
            {KOLONLAR.map((c) => {
                const raw = Number(gun[c.key]) || 0;
                // Türetilmiş mutabakat kolonları, gereken manuel girdi girilmeden
                // anlamsızdır (örn. Bahşiş = Σ Terminal − Modpos; terminal Z girilmeden
                // 0 − Modpos = büyük eksi gösterir). Bu durumda "—" göster.
                //  - Bahşiş (J): Σ Terminal (Q) girilmemişse (0).
                //  - Nakit Fark / Teslim: Kasada bulunan (teslim+devir) girilmemişse (0).
                const qVar = (Number(gun.kredi_karti_toplami) || 0) !== 0;
                const sayilanVar = (Number(gun.kasa_nakit) || 0) !== 0;  // kasada bulunan = teslim + devir
                let anlamsiz = false;
                if (c.key === 'kart_bahsis') anlamsiz = !qVar;
                else if (c.key === 'nakit_fark' || c.key === 'teslim_edilen_nakit') anlamsiz = !sayilanVar;

                const isNeg = c.signed && raw < 0 && !anlamsiz;
                // Mutabakat farkı: işaret bazlı renk (eksik=kırmızı, fazla=turuncu, uyumlu=yeşil).
                const farkAktif = c.fark && acik && !anlamsiz;
                const farkUyumlu = farkAktif && Math.abs(raw) < 0.005; // kuruş altı → uyumlu
                const farkVurgu = farkAktif && !farkUyumlu;            // sıfırdan farklı → dikkat
                let renk;
                if (!acik || anlamsiz) {
                    renk = '#cbd5e1';            // boş gün / henüz girilmemiş → çok soluk
                } else if (c.fark) {
                    // nakit farkı: 0=yeşil (uyumlu), +=turuncu (fazla), −=kırmızı (eksik)
                    renk = farkUyumlu ? FARK_OK_TONE : (raw > 0 ? FARK_FAZLA_TONE : FARK_EKSIK_TONE);
                } else if (isNeg) {
                    renk = BRAND;                // negatif bahşiş → kırmızı
                } else if (c.auto) {
                    renk = AUTO_TONE;            // sistem türevi → soluk
                } else {
                    renk = MANUAL_TONE;          // elle girilen → koyu
                }
                let govde;
                if (!acik || anlamsiz) {
                    govde = '—';
                } else if (farkVurgu) {
                    // fark kolonunda yön OK ile: ▲ fazla / ▼ eksik — kırmızı↔turuncu ayrımı
                    // şekilden de okunur (renk algısı/körlüğünden bağımsız, kesin işaret).
                    govde = `${raw > 0 ? '▲' : '▼'}${formatTl(Math.abs(raw))}`;
                } else {
                    govde = `${raw < 0 ? '-' : ''}${formatTl(Math.abs(raw))}`;
                }
                return (
                    <td
                        key={c.key}
                        style={{
                            ...numTd,
                            borderLeft: c.cash ? `2px solid ${cashKenar(acik)}` : numTd.borderLeft,
                            background: c.cash ? (acik ? '#f8fafc' : 'transparent') : 'transparent',
                            color: renk,
                            fontWeight: (!c.auto && acik) || isNeg || farkVurgu ? 600 : 400,
                        }}
                    >
                        {govde}
                    </td>
                );
            })}
        </tr>
    );
}

function haftaSonuRenk(haftaSonu, acik) {
    if (!acik) return '#cbd5e1';
    return haftaSonu ? '#b45309' : '#94a3b8';
}

function cashKenar(acik) {
    return acik ? CASH_ACCENT : '#cbd5e1';
}

// ── Ay özet barı (sticky) ─────────────────────────────────────────────
function OzetBar({ ozet, ay, yil, lokasyonAd, doluGun }) {
    const kalemler = [
        { etiket: 'Toplam Nakit', deger: ozet.toplam_nakit, vurgu: false },
        { etiket: 'Modpos Nakit', deger: ozet.toplam_modpos_nakit, vurgu: false, auto: true },
        { etiket: 'Kredi Kartı', deger: ozet.toplam_kredi_karti, vurgu: false },
        { etiket: 'Yemek Kartı', deger: ozet.toplam_yemek_karti, vurgu: false },
        { etiket: 'Teslim Edilen', deger: ozet.toplam_teslim, vurgu: false },
        { etiket: 'Gider', deger: ozet.toplam_gider, vurgu: true },
    ];
    return (
        <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            flexWrap: 'wrap',
            background: 'linear-gradient(180deg,#fff 0%,#fbfbfc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '8px 12px',
            boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
        }}>
            <div style={{
                fontSize: 12.5, fontWeight: 700, color: '#334155',
                paddingRight: 16, marginRight: 4, borderRight: '1px solid #e2e8f0',
                whiteSpace: 'nowrap',
            }}>
                {AYLAR[ay - 1]} {yil}
                {lokasyonAd && <span style={{ color: '#94a3b8', fontWeight: 500 }}> · {lokasyonAd}</span>}
                <div style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>
                    {doluGun} gün kayıtlı
                </div>
            </div>
            {kalemler.map((k, i) => (
                <div key={k.etiket} style={{
                    padding: '0 16px',
                    borderRight: i < kalemler.length - 1 ? '1px solid #f1f5f9' : 'none',
                    whiteSpace: 'nowrap',
                }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{k.etiket}</div>
                    <div style={{
                        fontSize: 15,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: k.vurgu ? BRAND : (k.auto ? AUTO_TONE : '#1f2937'),
                    }}>
                        {formatTl(k.deger)} <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>TL</span>
                    </div>
                </div>
            ))}
        </div>
    );
}
